'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import CartItem from './CartItem';
import OrderSummary from './OrderSummary';
import DeliveryOptions from './DeliveryOptions';
import SavedForLater from './SavedForLater';
import EmptyCart from './EmptyCart';
import { useProducts } from '@/hooks/useProducts';

interface CartItemData {
  id: string;
  name: string;
  image: string;
  alt: string;
  price: number;
  quantity: number;
  size?: string;
  flavor?: string;
  customization?: string;
}

interface SavedItem {
  id: string;
  name: string;
  image: string;
  alt: string;
  price: number;
}

interface SuggestedProduct {
  id: string;
  name: string;
  image: string;
  alt: string;
  price: number;
  category: string;
}

interface DeliveryOption {
  id: string;
  type: 'pickup' | 'delivery';
  label: string;
  description: string;
  fee: number;
  icon: string;
}

const ShoppingCartInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [cartItems, setCartItems] = useState<CartItemData[]>([]);
  const [savedItems, setSavedItems] = useState<SavedItem[]>([]);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<DeliveryOption | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState(0);

  // Fetch popular products from database
  const { products: fetchedProducts } = useProducts({ onlyPopular: true, limit: 4 });

  // Transform popular products to suggested products format
  const suggestedProducts: SuggestedProduct[] = useMemo(() => {
    return fetchedProducts.map(product => ({
      id: product.id,
      name: product.name,
      image: product.images[0]?.url || '',
      alt: product.images[0]?.alt || product.name,
      price: product.price,
      category: Array.isArray(product.category) ? product.category[0] : product.category
    }));
  }, [fetchedProducts]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const loadCartData = () => {
      const storedCart = localStorage.getItem('cart');
      if (storedCart) {
        setCartItems(JSON.parse(storedCart));
      }

      const storedSaved = localStorage.getItem('savedForLater');
      if (storedSaved) {
        setSavedItems(JSON.parse(storedSaved));
      }
    };

    loadCartData();
  }, [isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    localStorage.setItem('cart', JSON.stringify(cartItems));
    window.dispatchEvent(new Event('cartUpdated'));
  }, [cartItems, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    localStorage.setItem('savedForLater', JSON.stringify(savedItems));
  }, [savedItems, isHydrated]);

  const handleUpdateQuantity = (id: string, quantity: number) => {
    setCartItems((prev) =>
    prev.map((item) => item.id === id ? { ...item, quantity } : item)
    );
  };

  const handleRemoveItem = (id: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSaveForLater = (id: string) => {
    const item = cartItems.find((item) => item.id === id);
    if (item) {
      setSavedItems((prev) => [
      ...prev,
      {
        id: item.id,
        name: item.name,
        image: item.image,
        alt: item.alt,
        price: item.price
      }]
      );
      handleRemoveItem(id);
    }
  };

  const handleMoveToCart = (id: string) => {
    const item = savedItems.find((item) => item.id === id);
    if (item) {
      setCartItems((prev) => [
      ...prev,
      {
        ...item,
        quantity: 1
      }]
      );
      setSavedItems((prev) => prev.filter((saved) => saved.id !== id));
    }
  };

  const handleRemoveSaved = (id: string) => {
    setSavedItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleApplyCoupon = (code: string) => {
    const discountMap: Record<string, number> = {
      SWEET10: 0.1,
      BAKERY20: 0.2,
      FIRST15: 0.15
    };

    const discountPercent = discountMap[code.toUpperCase()] || 0;
    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setAppliedDiscount(subtotal * discountPercent);
  };

  const handleSelectDeliveryOption = (option: DeliveryOption) => {
    setSelectedDeliveryOption(option);
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) return;
    router.push('/order-confirmation');
  };

  const calculateSummary = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const tax = subtotal * 0.08;
    const deliveryFee = selectedDeliveryOption?.fee || 5.99;
    const total = subtotal + tax + deliveryFee - appliedDiscount;

    return {
      subtotal,
      tax,
      deliveryFee,
      discount: appliedDiscount,
      total
    };
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:189:6" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="189" data-component-end-line="203" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-28%20pb-16%22%7D" className="min-h-screen bg-background pt-28 pb-16">
        <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:190:8" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="190" data-component-end-line="202" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20px-5%20lg%3Apx-20%22%7D" className="mx-auto px-5 lg:px-20">
          <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:191:10" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="191" data-component-end-line="201" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:192:12" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="192" data-component-end-line="192" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-48%22%7D" className="h-8 bg-muted rounded w-48"></div>
            <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:193:12" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="193" data-component-end-line="200" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:194:14" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="194" data-component-end-line="198" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-4%22%7D" className="lg:col-span-2 space-y-4">
                {[1, 2, 3].map((i) =>
                <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:196:16" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="196" data-component-end-line="196" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-40%20bg-muted%20rounded%22%7D" key={i} className="h-40 bg-muted rounded"></div>
                )}
              </div>
              <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:199:14" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="199" data-component-end-line="199" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded%22%7D" className="h-96 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>);

  }

  if (cartItems.length === 0) {
    return (
      <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:209:6" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="209" data-component-end-line="213" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-28%20pb-16%22%7D" className="min-h-screen bg-background pt-28 pb-16">
        <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:210:8" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="210" data-component-end-line="212" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20px-5%20lg%3Apx-20%22%7D" className="mx-auto px-5 lg:px-20">
          <EmptyCart data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:211:10" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="211" data-component-end-line="211" data-component-file="ShoppingCartInteractive.tsx" data-component-name="EmptyCart" data-component-content="%7B%22elementName%22%3A%22EmptyCart%22%7D" suggestedProducts={suggestedProducts} />
        </div>
      </div>);

  }

  const summary = calculateSummary();

  return (
    <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:220:4" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="220" data-component-end-line="262" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
      {/* Cart Items and Order Summary */}
      <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:222:6" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="222" data-component-end-line="251" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:224:8" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="224" data-component-end-line="240" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-4%22%7D" className="lg:col-span-2 space-y-4">
          <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:225:10" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="225" data-component-end-line="229" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-4%22%7D" className="flex items-center justify-between mb-4">
            <h2 data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:226:12" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="226" data-component-end-line="228" data-component-file="ShoppingCartInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-heading%20font-semibold%20text-2xl%20text-foreground%22%2C%22textContent%22%3A%22Shopping%20Cart%20(%20)%22%7D" className="font-heading font-semibold text-2xl text-foreground">
              Shopping Cart ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
            </h2>
          </div>

          {cartItems.map((item) =>
          <CartItem data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:232:10" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="232" data-component-end-line="237" data-component-file="ShoppingCartInteractive.tsx" data-component-name="CartItem" data-component-content="%7B%22elementName%22%3A%22CartItem%22%7D"
            key={item.id}
            item={item}
            onUpdateQuantity={handleUpdateQuantity}
            onRemove={handleRemoveItem}
            onSaveForLater={handleSaveForLater} />

          )}
        </div>

        {/* Order Summary */}
        <div data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:243:8" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="243" data-component-end-line="250" data-component-file="ShoppingCartInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <OrderSummary data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:244:10" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="244" data-component-end-line="248" data-component-file="ShoppingCartInteractive.tsx" data-component-name="OrderSummary" data-component-content="%7B%22elementName%22%3A%22OrderSummary%22%7D"
            summary={summary}
            cartItems={cartItems}
            onApplyCoupon={handleApplyCoupon}
            onProceedToCheckout={handleProceedToCheckout} />

        </div>
      </div>

      {/* Delivery Options */}
      {/* <DeliveryOptions onSelectOption={handleSelectDeliveryOption} /> */}

      {/* Saved for Later */}
      <SavedForLater data-component-id="src/app/shopping-cart/components/ShoppingCartInteractive.tsx:257:6" data-component-path="src/app/shopping-cart/components/ShoppingCartInteractive.tsx" data-component-line="257" data-component-end-line="260" data-component-file="ShoppingCartInteractive.tsx" data-component-name="SavedForLater" data-component-content="%7B%22elementName%22%3A%22SavedForLater%22%7D"
        items={savedItems}
        onMoveToCart={handleMoveToCart}
        onRemove={handleRemoveSaved} />

    </div>);

};

export default ShoppingCartInteractive;